import React, { useEffect, useState } from 'react';
import { Row, Col, Accordion } from 'react-bootstrap';
import { Plus, Lock } from 'lucide-react';
import SelectedEquipmentTable from './SelectedEquipmentTable';
import UsageTips from './UsageTips';
import blockedEquipment from '../data/blockedEquipmentData';
import '../styles/Calculator.css';

const EquipmentList = ({ equipment, onEquipmentSelect, selectedEquipment, updateConsumption, consumption, selectedProduct, onQuoteClick }) => {
  const [selectedEquipments, setSelectedEquipments] = useState([]);

  useEffect(() => {
    if (Array.isArray(selectedEquipment)) {
      setSelectedEquipments(selectedEquipment);
      calculateTotalConsumption(selectedEquipment);
    } else {
      setSelectedEquipments([]);
    }
  }, [selectedEquipment]);

  const handleEquipmentOptionClick = (category, option) => {
    // Create a unique ID for this equipment instance
    const instanceId = Date.now() + Math.random();
    
    // Create the equipment object with the selected option
    const newEquipment = {
      ...category,
      instanceId,
      quantity: 1, 
      usage: option.utilization || 1,
      selectedOption: { ...option, categoryId: category.id }
    };
    
    // Add to selected equipment list
    const newSelected = [...selectedEquipments, newEquipment];
    setSelectedEquipments(newSelected);
    calculateTotalConsumption(newSelected);
    
    // Select this equipment
    onEquipmentSelect(newSelected);
  };

  const updateEquipment = (instanceId, field, value) => {
    const updatedEquipments = selectedEquipments.map(equip => {
      if (equip.instanceId === instanceId) {
        return { ...equip, [field]: parseFloat(value) };
      }
      return equip;
    });
    
    setSelectedEquipments(updatedEquipments);
    calculateTotalConsumption(updatedEquipments);
  };

  const updateEquipmentOption = (instanceId, option) => {
    const updatedEquipments = selectedEquipments.map(equip => {
      if (equip.instanceId === instanceId) {
        return { 
          ...equip, 
          selectedOption: { ...option, categoryId: equip.id },
          usage: option.utilization || equip.usage || 1
        };
      }
      return equip;
    });
    
    setSelectedEquipments(updatedEquipments);
    calculateTotalConsumption(updatedEquipments);
  };

  const calculateTotalConsumption = (equipments) => {
    if (!Array.isArray(equipments)) return;
    const total = equipments.reduce((sum, equip) => sum + getEquipmentConsumption(equip), 0);
    updateConsumption(total);
  };

  const removeEquipment = (instanceId) => {
    const updatedEquipments = selectedEquipments.filter(equip => equip.instanceId !== instanceId);
    setSelectedEquipments(updatedEquipments);
    calculateTotalConsumption(updatedEquipments);
    
    if (selectedEquipment && selectedEquipment.instanceId === instanceId) {
      onEquipmentSelect(null);
    }
  };

  function getEquipmentConsumption(equip) {
    const power = equip.selectedOption?.watt || equip.puissance || 0;
    const quantity = equip.quantity || 1;
    // Électroménager options already hold the daily total (e.g. 600 Wh for 12 h/jour).
    const usage = equip.totalGiven ? 1 : (equip.usage || 1);
    return power * quantity * usage;
  }

  const isEquipmentBlocked = (productId, equipmentId, optionId = null) => {
    if (productId === null || productId === undefined) return false;
    const productRule = blockedEquipment.find(p => p.productId === productId);
    if (!productRule) return false;

    const equipRule = productRule.blockedEquipment.find(e => e.equipmentId === equipmentId);
    if (!equipRule) return false;

    if (equipRule.blockedOptions.includes('ALL')) return true;

    if (optionId !== null) return equipRule.blockedOptions.includes(optionId);
    return false;
  };

  const resetAll = () => {
    setSelectedEquipments([]);
    updateConsumption(0);
    onEquipmentSelect([]);
  };

  return (
    <Row className="calc-body g-4">
      <Col lg={6}>
        <div className="calc-title-block">
          <h3 className="calc-title">Liste des équipements</h3>
          <p className="calc-subtitle">Cliquez sur un équipement pour l'ajouter à votre bilan</p>
        </div>
        <Accordion defaultActiveKey="0" className="equip-accordion">
          {equipment.map((category, index) => (
            <Accordion.Item key={category.id} eventKey={index.toString()}>
              <Accordion.Header>
                <img
                  src={category.image}
                  alt=""
                  className="equip-accordion__icon"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/30/3D5A73/FFFFFF?text=⚡';
                  }}
                />
                {category.name}
              </Accordion.Header>
              <Accordion.Body>
                <div className="equip-grid">
                  {category.options.map(option => {
                    const isBlocked = isEquipmentBlocked(selectedProduct?.id, category.id, option.id);

                    return (
                      <button
                        key={option.id}
                        type="button"
                        className="equip-option"
                        disabled={isBlocked}
                        title={isBlocked ? 'Non disponible pour ce pack' : `Ajouter ${option.name}`}
                        onClick={() => handleEquipmentOptionClick(category, option)}
                      >
                        <span className="equip-option__thumb">
                          <img
                            src={option.image}
                            alt=""
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/40/3D5A73/FFFFFF?text=⚡';
                            }}
                          />
                        </span>
                        <span className="equip-option__body">
                          <span className="equip-option__name">{option.name.trim()}</span>
                          <span className="equip-option__meta">
                            {option.watt} W
                            {option.utilization && ` · ${option.utilization} h/jour`}
                          </span>
                        </span>
                        <span className="equip-option__action" aria-hidden="true">
                          {isBlocked ? <Lock size={14} /> : <Plus size={16} />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      </Col>

      <Col lg={6}>
        <div className="calc-sidebar">
          <SelectedEquipmentTable
            productName={selectedProduct?.name}
            selectedEquipments={selectedEquipments}
            consumption={consumption}
            production={selectedProduct?.puissance || 0}
            getEquipmentConsumption={getEquipmentConsumption}
            updateEquipment={updateEquipment}
            updateEquipmentOption={updateEquipmentOption}
            removeEquipment={removeEquipment}
            onReset={resetAll}
            onQuoteClick={onQuoteClick}
          />
        </div>
      </Col>

      <Col xs={12}>
        <UsageTips production={selectedProduct?.puissance || 0} />
      </Col>
    </Row>
  );
};

export default EquipmentList;
